const BASE_URL = import.meta.env.VITE_API_URL ?? "";

interface ApiListResponse<T> {
  data: T[];
  page?: { limit: number; offset: number };
}

interface ApiSingleResponse<T> {
  data: T;
}

interface ApiErrorBody {
  requestId?: string;
  error: { code: string; message: string; details?: unknown };
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: unknown,
    public readonly requestId?: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  // Attach the JWT when a session exists. Without it the API server's
  // dev-mode fallback synthesises a stub user (local dev only).
  let token: string | null = null;
  try {
    const raw = localStorage.getItem("sh.session");
    if (raw) token = (JSON.parse(raw) as { token?: string }).token ?? null;
  } catch {
    // ignore storage failures
  }
  const res = await fetch(`${BASE_URL}${path}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  if (!res.ok) {
    let parsed: ApiErrorBody | undefined;
    try {
      parsed = (await res.json()) as ApiErrorBody;
    } catch {
      // response wasn't JSON
    }
    throw new ApiError(
      res.status,
      parsed?.error?.code ?? "HTTP_ERROR",
      parsed?.error?.message ?? `${method} ${path} → ${res.status}`,
      parsed?.error?.details,
      parsed?.requestId,
    );
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const api = {
  list<T>(path: string) {
    return request<ApiListResponse<T>>("GET", path);
  },
  get<T>(path: string) {
    return request<ApiSingleResponse<T>>("GET", path);
  },
  post<T>(path: string, body: unknown) {
    return request<ApiSingleResponse<T>>("POST", path, body);
  },
  patch<T>(path: string, body: unknown) {
    return request<ApiSingleResponse<T>>("PATCH", path, body);
  },
  del(path: string) {
    return request<void>("DELETE", path);
  },
};
